import AdminSetup from './components/AdminSetup';
import React, { lazy, Suspense, useEffect } from 'react';
import CookieConsent from './components/CookieConsent';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Home from './pages/Home';
import ScrollToTop from './components/ScrollToTop';
import { AuthContextProvider, UserAuth } from './context/AuthContext';
import { ChatroomProvider } from './context/ChatroomContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import TrackingParamCleaner from './components/TrackingParamCleaner';
import CursorTrail from './components/CursorTrail/CursorTrail';
import { clearAutomationPackageCache, loadAutomationPackages } from './utils/automationPackageCache';

// Keep the candlesticks disabled while preserving the proximity glow.
const CURSOR_TRAIL_ENABLED = false;

const loadAccountPage = () => import('./pages/Account');
const Account = lazy(loadAccountPage);
const Chatroom = lazy(() => import('./pages/Chatroom'));
const Automation = lazy(() => import('./pages/Automation'));
const Backtesting = lazy(() => import('./pages/Backtesting'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogArchive = lazy(() => import('./pages/BlogArchive'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const Company = lazy(() => import('./pages/Company'));
const Contact = lazy(() => import('./pages/Contact'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Energy = lazy(() => import('./pages/Energy'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Login = lazy(() => import('./pages/Login'));
const Macro = lazy(() => import('./pages/Macro'));
const Metals = lazy(() => import('./pages/Metals'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Register = lazy(() => import('./pages/Register'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Report = lazy(() => import('./pages/Report'));
const Retail = lazy(() => import('./pages/Retail'));
const Signals = lazy(() => import('./pages/Signals'));
const Tech = lazy(() => import('./pages/Tech'));
const Terms = lazy(() => import('./pages/Terms'));
const DesktopAuthorize = lazy(() => import('./pages/DesktopAuthorize'));

const RouteLoadingFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-[#12181B] text-sm font-semibold text-white/55" role="status">
    Loading…
  </div>
);

const SignedInRoutePreloader = () => {
  const { user } = UserAuth();

  useEffect(() => {
    if (user?.uid) {
      loadAccountPage();
      loadAutomationPackages(user.uid).catch(() => {});
    } else {
      clearAutomationPackageCache();
    }
    return clearAutomationPackageCache;
  }, [user?.uid]);

  return null;
};

const App = () => {
  return (
    <Router>
      <AuthContextProvider>
        <ChatroomProvider>
        <SignedInRoutePreloader />
        <CursorTrail enableParticles={CURSOR_TRAIL_ENABLED} />
        <CookieConsent />
        <ScrollToTop />
        <TrackingParamCleaner />
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route path="/desktop/authorize" element={<DesktopAuthorize />} />
            <Route path="/" element={<Home />} />
            <Route path="/backtesting" element={<Backtesting />} />
            <Route path="/signals" element={<Signals />} />
            <Route path="/macro" element={<Macro />} />
            <Route path="/automation" element={<Automation />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/company" element={<Company />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/archive" element={<BlogArchive />} />
            <Route path="/blog/:slug" element={<BlogPage />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/signin" element={<Login />} />
            <Route path="/chatroom" element={<ProtectedRoute><Chatroom /></ProtectedRoute>} />
            <Route path="/signup" element={<Register />} />
            <Route path="/account/security" element={<ProtectedRoute><AdminSetup /></ProtectedRoute>} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/:jobId"
              element={
                <ProtectedRoute>
                  <Report />
                </ProtectedRoute>
              }
            />
            <Route
              path="/Metals"
              element={
                <ProtectedRoute requiredPortfolio="metals">
                  <Metals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/Retail"
              element={
                <ProtectedRoute requiredPortfolio="retail">
                  <Retail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/Energy"
              element={
                <ProtectedRoute requiredPortfolio="energy">
                  <Energy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/Tech"
              element={
                <ProtectedRoute requiredPortfolio="tech">
                  <Tech />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <AdminRoute>
                  <Dashboard />
                </AdminRoute>
              }
            />
          </Routes>
        </Suspense>
        </ChatroomProvider>
      </AuthContextProvider>
    </Router>
  );
};

export default App;
